
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Scale, Shield, Users, FileText, Clock, AlertTriangle } from 'lucide-react';

const ProfessionalServicesPEO = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-purple-50">
      <Helmet>
        <title>PEO Services for Professional Services | Law Firms, Accounting, Consulting | The PEO App</title>
        <meta name="description" content="Specialized PEO services for professional services firms including law firms, accounting practices, and consulting companies. Expert HR compliance and professional liability management." />
        <link rel="canonical" href="https://thepeoapp.com/industries/professional-services" />
        <meta name="keywords" content="professional services PEO, law firm PEO, accounting firm PEO, consulting PEO, professional liability insurance, attorney HR services" />
        
        <meta property="og:title" content="PEO Services for Professional Services Firms" />
        <meta property="og:description" content="Specialized PEO solutions for law firms, accounting practices, and consulting companies with professional liability expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/professional-services" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Scale className="h-16 w-16 text-purple-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Professional Services
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Specialized Professional Employer Organization services for law firms, accounting practices, 
              consulting companies, and other professional service providers. Expert HR with professional liability focus.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Professional Services Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Professional Liability Risk</h3>
                    <p className="text-gray-700">Managing malpractice insurance and professional liability exposure</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileText className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Client Confidentiality</h3>
                    <p className="text-gray-700">Maintaining strict confidentiality in all HR processes and documentation</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Billable Hour Pressure</h3>
                    <p className="text-gray-700">High-stress environment with demanding client service expectations</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Partner vs. Associate Structure</h3>
                    <p className="text-gray-700">Complex compensation structures and equity participation arrangements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Regulatory Compliance</h3>
                    <p className="text-gray-700">Industry-specific regulations and continuing education requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Scale className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Work-Life Balance</h3>
                    <p className="text-gray-700">High turnover due to demanding schedules and burnout</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Professional Services PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-purple-50 p-6 rounded-xl">
                <Shield className="h-12 w-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Professional Liability Coverage</h3>
                <p className="text-gray-700">
                  Specialized malpractice insurance and professional liability coverage 
                  tailored to your practice area and risk profile.
                </p>
              </div>
              
              <div className="bg-purple-50 p-6 rounded-xl">
                <FileText className="h-12 w-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Confidential HR Management</h3>
                <p className="text-gray-700">
                  Secure, confidential HR processes that understand the sensitive 
                  nature of professional services work and client relationships.
                </p>
              </div>
              
              <div className="bg-purple-50 p-6 rounded-xl">
                <Users className="h-12 w-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Flexible Compensation</h3>
                <p className="text-gray-700">
                  Support for complex compensation structures including profit sharing, 
                  equity participation, and performance-based bonuses.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Professional Services PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand the unique challenges of professional services 
              and have experience with law firms, accounting practices, and consulting companies.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Professional Services PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Professional Services We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Law Firms</h3>
                <p className="text-sm text-gray-600">Corporate, litigation, family, personal injury</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Accounting Firms</h3>
                <p className="text-sm text-gray-600">CPA practices, tax preparation, auditing</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Consulting</h3>
                <p className="text-sm text-gray-600">Management, IT, financial, strategy consulting</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Other Services</h3>
                <p className="text-sm text-gray-600">Architecture, engineering, real estate</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProfessionalServicesPEO;
